import { Dropdown, Form } from "react-bootstrap";
import "bootstrap-daterangepicker/daterangepicker.css";
import "assets/css/daterangepicker.css";
import { ChangeEvent, FC, useEffect, useState } from "react";
import { forEach, map } from "lodash";
import localStorageService from "service/localStorageService";
import { getLocalTimezone } from "utils/functions";
import { CustomDropdown } from ".";

export type TimezoneDropdownProps = {
    value?: string;
    onChange?: (value: string) => void;
    disabled?: boolean;
    className?: string;
};

const sortTimeZoneDict = (timeZoneDict: Record<string, string>) => {
    return Object.keys(timeZoneDict)
        .sort((a, b) => a.localeCompare(b))
        .reduce((obj, key) => {
            obj[key] = timeZoneDict[key];
            return obj;
        }, {} as Record<string, string>);
};

const TimezoneDropdown: FC<TimezoneDropdownProps> = ({
    value,
    onChange,
    disabled,
    ...restProps
}) => {
    const [selected, setSelected] = useState<string>(getLocalTimezone());
    const [searchText, setSearchText] = useState("");
    const [timeZoneDict, setTimeZoneDict] = useState<Record<string, string>>(
        localStorageService.getItem("timezones")
    );
    const timeZoneObject = localStorageService.getItem("timezones");

    const displayTimezone = (timezone: string) => {
        const timeOffset = timeZoneObject[timezone];
        return `${timezone} (UTC ${timeOffset})`;
    };

    useEffect(() => {
        const sortedTimeZones = sortTimeZoneDict(timeZoneObject);
        setTimeZoneDict(sortedTimeZones);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        value && setSelected(value);
    }, [value]);

    const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setSearchText(value);
        _filter(value);
    };

    const _filter = (search: string) => {
        let filtered: Record<string, string> = {};
        forEach(timeZoneObject, (value: string, key: string) => {
            if (
                !search ||
                `${key} (UTC ${value})`
                    .toLowerCase()
                    .includes(search.toLowerCase())
            ) {
                filtered[key] = value;
            }
        });

        const sortedFiltered = sortTimeZoneDict(filtered);
        setTimeZoneDict(sortedFiltered);
    };

    const handleSelect = (value: string | null, e: Object) => {
        const _value = value ?? "";
        setSelected(_value);
        onChange?.(_value);
    };

    const onToggle = (isOpen: boolean) => {
        if (!isOpen) {
            setSearchText("");
            _filter("");
        }
    };

    return (
        <CustomDropdown
            {...restProps}
            onSelect={handleSelect}
            onToggle={onToggle}
        >
            <Dropdown.Toggle disabled={disabled}>
                {selected && displayTimezone(selected)}
                <i className="material-icons float-right">expand_more</i>
            </Dropdown.Toggle>
            <Dropdown.Menu className="w-100">
                <Dropdown.Header>
                    <Form.Control
                        autoFocus
                        className="search w-100"
                        placeholder="Search for region/city"
                        aria-label="search"
                        value={searchText}
                        onChange={handleSearch}
                    />
                </Dropdown.Header>
                <div className="dropdown-menu-content">
                    {map(timeZoneDict, (value: string, key: string) => (
                        <Dropdown.Item
                            key={key}
                            eventKey={key}
                            active={key === selected}
                        >
                            {key} (UTC {value})
                        </Dropdown.Item>
                    ))}
                </div>
            </Dropdown.Menu>
        </CustomDropdown>
    );
};

export default TimezoneDropdown;
